import api from './apiClient';

export const patientService = {
    /**
     * Obtener lista de pacientes con búsqueda y filtros
     */
    getAll: async (params = {}) => {
        try {
            const response = await api.get('/patients-expanded', { params });
            return response.data;
        } catch (error) {
            console.error('Error al obtener pacientes:', error);
            throw error;
        }
    },

    /**
     * Obtener un paciente por ID con todas sus relaciones
     */
    getById: async (id) => {
        try {
            const response = await api.get(`/patients-expanded/${id}`);
            return response.data;
        } catch (error) {
            console.error(`Error al obtener paciente ${id}:`, error);
            throw error;
        }
    },

    /**
     * Crear nuevo paciente
     */
    create: async (data) => {
        try {
            const response = await api.post('/patients-expanded', data);
            return response.data;
        } catch (error) {
            console.error('Error al crear paciente:', error);
            throw error;
        }
    },

    /**
     * Actualizar paciente
     */
    update: async (id, data) => {
        try {
            const response = await api.put(`/patients-expanded/${id}`, data);
            return response.data;
        } catch (error) {
            console.error(`Error al actualizar paciente ${id}:`, error);
            throw error;
        }
    },

    /**
     * Eliminar paciente (soft delete)
     */
    delete: async (id) => {
        try {
            const response = await api.delete(`/patients-expanded/${id}`);
            return response.data;
        } catch (error) {
            console.error(`Error al eliminar paciente ${id}:`, error);
            throw error;
        }
    },

    // ========== ALERGIAS ==========

    /**
     * Obtener alergias de un paciente
     */
    getAllergies: async (patientId) => {
        try {
            const response = await api.get(`/patients/${patientId}/allergies`);
            return response.data;
        } catch (error) {
            console.error(`Error al obtener alergias del paciente ${patientId}:`, error);
            throw error;
        }
    },

    /**
     * Agregar alergia
     */
    addAllergy: async (data) => {
        try {
            const response = await api.post('/allergies', data);
            return response.data;
        } catch (error) {
            console.error('Error al agregar alergia:', error);
            throw error;
        }
    },

    /**
     * Eliminar alergia
     */
    deleteAllergy: async (id) => {
        try {
            const response = await api.delete(`/allergies/${id}`);
            return response.data;
        } catch (error) {
            console.error(`Error al eliminar alergia ${id}:`, error);
            throw error;
        }
    },

    // ========== NOTAS CLÍNICAS ==========

    /**
     * Obtener notas clínicas de un paciente
     */
    getClinicalNotes: async (patientId, limit = 10) => {
        try {
            const response = await api.get(`/patients/${patientId}/clinical-notes`, {
                params: { limit }
            });
            return response.data;
        } catch (error) {
            console.error(`Error al obtener notas clínicas del paciente ${patientId}:`, error);
            throw error;
        }
    },

    /**
     * Obtener una nota clínica específica
     */
    getClinicalNote: async (id) => {
        try {
            const response = await api.get(`/clinical-notes/${id}`);
            return response.data;
        } catch (error) {
            console.error(`Error al obtener nota clínica ${id}:`, error);
            throw error;
        }
    },

    /**
     * Crear nota clínica SOAP
     */
    createClinicalNote: async (data) => {
        try {
            const response = await api.post('/clinical-notes', data);
            return response.data;
        } catch (error) {
            console.error('Error al crear nota clínica:', error);
            throw error;
        }
    },

    /**
     * Actualizar nota clínica (solo si no está firmada)
     */
    updateClinicalNote: async (id, data) => {
        try {
            const response = await api.put(`/clinical-notes/${id}`, data);
            return response.data;
        } catch (error) {
            console.error(`Error al actualizar nota clínica ${id}:`, error);
            throw error;
        }
    },

    /**
     * Firmar nota clínica
     */
    signClinicalNote: async (id) => {
        try {
            const response = await api.post(`/clinical-notes/${id}/sign`);
            return response.data;
        } catch (error) {
            console.error(`Error al firmar nota clínica ${id}:`, error);
            throw error;
        }
    },

    // ========== RECETAS ==========

    /**
     * Obtener recetas de un paciente
     */
    getPrescriptions: async (patientId) => {
        try {
            const response = await api.get(`/patients/${patientId}/prescriptions`);
            return response.data;
        } catch (error) {
            console.error(`Error al obtener recetas del paciente ${patientId}:`, error);
            throw error;
        }
    },

    /**
     * Obtener una receta específica
     */
    getPrescription: async (id) => {
        try {
            const response = await api.get(`/prescriptions/${id}`);
            return response.data;
        } catch (error) {
            console.error(`Error al obtener receta ${id}:`, error);
            throw error;
        }
    },

    /**
     * Crear receta médica
     */
    createPrescription: async (data) => {
        try {
            const response = await api.post('/prescriptions', data);
            return response.data;
        } catch (error) {
            console.error('Error al crear receta:', error);
            throw error;
        }
    },

    /**
     * Obtener receta para imprimir
     */
    printPrescription: async (id) => {
        try {
            const response = await api.get(`/prescriptions/${id}/print`);
            return response.data;
        } catch (error) {
            console.error(`Error al obtener receta para imprimir ${id}:`, error);
            throw error;
        }
    },

    // ========== LABORATORIO ==========

    /**
     * Obtener órdenes de laboratorio de un paciente
     */
    getLabOrders: async (patientId) => {
        try {
            const response = await api.get(`/patients/${patientId}/lab-orders`);
            return response.data;
        } catch (error) {
            console.error(`Error al obtener órdenes de laboratorio del paciente ${patientId}:`, error);
            throw error;
        }
    },

    /**
     * Obtener una orden de laboratorio específica
     */
    getLabOrder: async (id) => {
        try {
            const response = await api.get(`/lab-orders/${id}`);
            return response.data;
        } catch (error) {
            console.error(`Error al obtener orden de laboratorio ${id}:`, error);
            throw error;
        }
    },

    /**
     * Crear orden de laboratorio
     */
    createLabOrder: async (data) => {
        try {
            const response = await api.post('/lab-orders', data);
            return response.data;
        } catch (error) {
            console.error('Error al crear orden de laboratorio:', error);
            throw error;
        }
    },

    /**
     * Actualizar estado de orden de laboratorio
     */
    updateLabOrderStatus: async (id, status) => {
        try {
            const response = await api.put(`/lab-orders/${id}/status`, { status });
            return response.data;
        } catch (error) {
            console.error(`Error al actualizar estado de orden ${id}:`, error);
            throw error;
        }
    },

    /**
     * Obtener catálogo de estudios de laboratorio
     */
    getLabTestCatalog: async () => {
        try {
            const response = await api.get('/lab-test-catalog');
            return response.data;
        } catch (error) {
            console.error('Error al obtener catálogo de estudios:', error);
            throw error;
        }
    },

    /**
     * Obtener catálogo de medicamentos
     */
    getMedicationCatalog: async () => {
        try {
            const response = await api.get('/medication-catalog');
            return response.data;
        } catch (error) {
            console.error('Error al obtener catálogo de medicamentos:', error);
            throw error;
        }
    }
};

export default patientService;
